import { makeAutoObservable, observable, runInAction } from "mobx";
import {
  deleteSensor,
  editSensor,
  generateSensors,
  getSensorsForSensorBatch,
  postImportNewSensors,
  toggleManufSensor,
} from "../services/sensor-services";
import {
  ImportSensorsDto,
  Sensor,
  EditSensorDto,
  GenerateSensorsDto,
  DeleteSensorDto,
} from "features/iot/sensors/models/sensor-models";
import { StoreResults } from "stores/stores-helper";
import SensorBatchStore from "../../sensor-batch/stores/sensor-batch-store";
import { downloadFileFromBlobUrl } from "components/utils/files-utils";
import { convertDDMMMYYYYtoDate, getTodayDateString } from "components/utils/date-formatter-utils";

import {
  mapSensorToSBSensor,
  updateSBSensorWithSensor,
} from "features/iot/sensors/utils/sensors-mapping";
import { ConvertSensorsToCsv } from "features/iot/sensors/components/sensor-import-modal/utils/sensor-csv-reader";
import { SBSensor } from "features/iot/sensor-batch/models/sensor-batch-models";

class SensorStore {
  sensorMap: Record<string, Sensor> = observable.object({});
  sensorLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  get sensors() {
    return Object.values(this.sensorMap);
  }
  getSensorByMacAddress(macAddress: string): Sensor | undefined {
    return Object.values(this.sensorMap).find(
      (sensor) => sensor.macAddress === macAddress
    );
  }

  loadSensors = async (sensorBatchId: string) => {
    try {
      if (this.sensorLoading) {
        return;
      }

      runInAction(() => {
        this.sensorLoading = true;
      });

      const data = (await getSensorsForSensorBatch(sensorBatchId)) || [];
      runInAction(() => {
        data.forEach((sensor: Sensor) => {
          this.sensorMap[sensor.macAddress] = sensor;
        });

        this.sensorLoading = false;
      });
    } catch (error) {
      console.error(
        `Failed to load sensors for batch ${sensorBatchId}:`,
        error
      );
    } finally {
      runInAction(() => {
        this.sensorLoading = false;
      });
    }
  };

  createEntity = async (
    entities: Partial<ImportSensorsDto>
  ): Promise<StoreResults> => {
    if (this.sensorLoading) {
      return { passed: false, message: "", entityId: "" };
    }

    runInAction(() => {
      this.sensorLoading = true;
    });

    try {
      const response = await postImportNewSensors(entities);
      if (response.statusCode === 200) {
        const createdSensors = response.result as Sensor[];
        const sensorBatchId = entities.sensorBatchId as string;

        // Download the generated sensors as CSV
        DownloadGeneratedSensors("createdSensors", createdSensors);

        runInAction(() => {
          // Add sensors to the sensorMap
          createdSensors.forEach((sensorDto) => {
            this.sensorMap[sensorDto.macAddress] = observable.object(sensorDto);
          });

          // Retrieve existing sensors in the batch from sensorBatchStore
          const existingSBSensors =
            SensorBatchStore.sensorBatchMap[sensorBatchId]?.sensors || [];

          // Map created sensors to the sensor batch, updating details from the entities
          const updatedSBSensors = createdSensors.map((sensor) => {
            const existingSBSensor = existingSBSensors.find(
              (s) => s.macAddress === sensor.macAddress
            );
            const mappedSensor = existingSBSensor
              ? updateSBSensorWithSensor(sensor, existingSBSensor)
              : mapSensorToSBSensor(sensor);

            // Find the corresponding entity details in the original entities
            const newSensor = entities?.sensors?.find(
              (sb) => sb.macAddress === mappedSensor.macAddress
            );

            // Update sensor properties based on provided entity details
            if (newSensor) {
              mappedSensor.isManufactured = newSensor?.isManufactured as boolean;
              mappedSensor.manufacturedDate = convertDDMMMYYYYtoDate(
                newSensor?.manufacturedDate as string
              )??undefined;
              mappedSensor.firmwareRevision = newSensor?.firmwareRevision as string;
              mappedSensor.hardwareRevision = newSensor?.hardwareRevision as string;
              mappedSensor.offset = newSensor?.offset as number;
              mappedSensor.pitch = newSensor?.pitch as number;
              mappedSensor.numberOfResistiveElements =
              newSensor?.numberOfResistiveElements as number;
            }

            return mappedSensor;
          });

          // Update the sensors in the sensorBatchStore with the updated sensors
          SensorBatchStore.updateSensorsInBatch(sensorBatchId, [
            ...existingSBSensors,
            ...updatedSBSensors,
          ]);
        });

        return {
          passed: true,
          message: "",
          entityId: createdSensors.map((s) => s.macAddress).join(", "),
        };
      } else if (response.statusCode === 409) {
        return {
          passed: false,
          message: "Conflict-409: a sensor with the same identifier already exists.",
          entityId: "",
        };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message, entityId: "" };
      }
    } catch (error) {
      console.error("Error creating sensor:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
        entityId: "",
      };
    } finally {
      runInAction(() => {
        this.sensorLoading = false;
      });
    }
  };

  editEntity = async (
    entity: Partial<EditSensorDto>
  ): Promise<StoreResults> => {
    if (this.sensorLoading) {
      return { passed: false, message: "", entityId: "" };
    }
    runInAction(() => {
      this.sensorLoading = true;
    });

    try {
      const response = await editSensor(entity);
      if (response.statusCode === 200) {
        const sensorBatchId = entity.sensorBatchId;

        runInAction(() => {
          const updatedSensor = this.getSensorByMacAddress(
            entity?.macAddress ?? ""
          );
          if (sensorBatchId) {
            const existingSBSensors =
              SensorBatchStore.sensorBatchMap[sensorBatchId]?.sensors || [];

            // Map through the existing batch sensors and update the matching one
            const updatedSensors = existingSBSensors.map((sbSensor) =>
              sbSensor.macAddress === entity.macAddress
                ? {
                    ...sbSensor, // Preserve existing properties of the batch sensor
                    serialNumber: entity.serialNumber||"",
                    firmwareRevision: entity.firmwareRevision ||"",
                    hardwareRevision: entity.hardwareRevision||"",
                    offset: entity.offset||0,
                    pitch: entity.pitch||0,
                    isManufactured: entity.isManufactured||false,
                    manufacturedDate: new Date(entity.manufacturedDate||""),
                    numberOfResistiveElements:entity.numberOfResistiveElements||0
                  }
                : sbSensor
            );
            // Persist the updated sensors in the SensorBatchStore
            SensorBatchStore.updateSensorsInBatch(
              sensorBatchId,
              updatedSensors
            );
          }
          if (updatedSensor) {
            // Manually update the fields of the sensor in the sensorMap
            this.sensorMap[updatedSensor.macAddress] = {
              ...updatedSensor, // Preserve existing properties
              serialNumber: entity.serialNumber as string,
            };
          }
        });
        return {
          passed: true,
          message: "",
        };
      } else if (response.statusCode === 409) {
        return {
          passed: false,
          message:
            "Conflict-409: a sensor with the same identifier already exists.",
          entityId: "",
        };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message, entityId: "" };
      }
    } catch (error) {
      console.error("Error creating sensor:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
        entityId: "",
      };
    } finally {
      runInAction(() => {
        this.sensorLoading = false;
      });
    }
  };

  deleteEntity = async (
    deleteSensorDto:DeleteSensorDto
  ): Promise<StoreResults> => {
    if (this.sensorLoading) {
        return { passed: false, message: "" };
    }

    runInAction(() => {
      this.sensorLoading = true;
    });

    try {
      const macAddress=deleteSensorDto.macAddress;
      const sensorBatchId=deleteSensorDto.sensorBatchId;
      console.log("Here",macAddress,sensorBatchId);
      const response = await deleteSensor(deleteSensorDto);
      if (response.statusCode === 200) {
        runInAction(() => {
          delete this.sensorMap[macAddress];

          // Update SensorBatchStore by removing the sensor
          const updatedSensors = SensorBatchStore.sensorBatchMap[
            sensorBatchId
          ]?.sensors.filter((sensor) => sensor.macAddress !== macAddress);
          SensorBatchStore.updateSensorsInBatch(
            sensorBatchId,
            updatedSensors || []
          );
        });

        return { passed: true, message: "" };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message };
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
      };
    } finally {
      runInAction(() => {
        this.sensorLoading = false;
      });
    }
  };

  toggleSensorsManuf = async (
    entities: Partial<EditSensorDto[]>
  ): Promise<number> => {
    if (this.sensorLoading) {
      return 400; // Bad Request
    }

    try {
      runInAction(() => {
        this.sensorLoading = true;
      });

      const response = await toggleManufSensor(entities);
      if (response.statusCode === 200) {
        runInAction(() => {
          entities.forEach((entity) => {
            const sensorBatchId = entity?.sensorBatchId!;
            const updatedSensors = SensorBatchStore.sensorBatchMap[
              sensorBatchId
            ]?.sensors.map((s) => {
              if (s.macAddress === entity?.macAddress) {
                return {
                  ...s,
                  isManufactured: entity.isManufactured!,
                  manufacturedDate: entity.isManufactured
                    ? new Date(entity.manufacturedDate!)
                    : undefined,
                };
              }
              return s;
            });

            SensorBatchStore.updateSensorsInBatch(
              sensorBatchId,
              updatedSensors || []
            );
          });
        });

        return 200; // OK
      } else {
        return response.statusCode; // Return the status code from the response
      }
    } catch (error) {
      console.error("Error toggling sensor manufacturing status:", error);
      return 500; // Internal Server Error
    } finally {
      runInAction(() => {
        this.sensorLoading = false;
      });
    }
  };

  generateSensors = async (
    entity: Partial<GenerateSensorsDto>
  ): Promise<StoreResults> => {
    if (this.sensorLoading) {
      return { passed: false, message: "", entityId: "" };
    }

    try {
      runInAction(() => {
        this.sensorLoading = true;
      });

      const response = await generateSensors(entity);
      if (response.statusCode === 200) {
        const listOfSensors = response.result as Sensor[];
        const sensorBatchId = entity.sensorBatchId as string;

        runInAction(() => {
          listOfSensors.forEach((sensorDto) => {
            this.sensorMap[sensorDto.macAddress] = observable.object(sensorDto);
          });

          // Update SensorBatchStore with generated sensors
          const existingSBSensors =
            SensorBatchStore.sensorBatchMap[sensorBatchId]?.sensors || [];

          const updatedSBSensors = listOfSensors.map((sensor) => {
            const existingSBSensor = existingSBSensors.find(
              (s) => s.macAddress === sensor.macAddress
            );
            return existingSBSensor
              ? updateSBSensorWithSensor(sensor, existingSBSensor)
              : mapSensorToSBSensor(sensor);
          });

          SensorBatchStore.updateSensorsInBatch(sensorBatchId, [
            ...existingSBSensors,
            ...updatedSBSensors,
          ]);

          DownloadGeneratedSensors("NewGeneratedSensors", listOfSensors);
        });

        return { passed: true, message: "" };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message };
      }
    } catch (error) {
      console.error("Error generating sensors:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
        entityId: "",
      };
    } finally {
      runInAction(() => {
        this.sensorLoading = false;
      });
    }
  };
}

// This is outside any class or object
export function DownloadGeneratedSensors(name: string, sensors: SBSensor[]|Sensor[]) {
  // Generate CSV
  const csvContent = ConvertSensorsToCsv(sensors);
  // Create a Blob from the CSV string
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  // Create a Blob URL
  const blobUrl = URL.createObjectURL(blob);
  // Trigger download
  downloadFileFromBlobUrl(blobUrl, `${name}-${getTodayDateString()}.csv`);
}

export default new SensorStore();
